// Renders a shareable BCH School completion certificate onto a canvas.
// Pure canvas drawing (no external libs) so it's CSP-safe and dependency-free.

export interface CertificateData {
  name: string;
  xp: number;
  totalXp: number;
  badgeCount: number;
  dateStr: string;
}

const WIDTH = 1200;
const HEIGHT = 630; // classic OG / social-card aspect ratio

/** Draw a rounded rectangle path. */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Draws the certificate and returns the canvas. Kept synchronous and simple —
 * uses system-safe fonts so there's nothing to preload.
 */
export function drawCertificate(data: CertificateData): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = WIDTH * dpr;
  canvas.height = HEIGHT * dpr;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);

  // Background gradient
  const bg = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
  bg.addColorStop(0, "#f0fdf4");
  bg.addColorStop(1, "#dcfce7");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // Decorative corner circles
  ctx.fillStyle = "rgba(34,197,94,0.12)";
  ctx.beginPath();
  ctx.arc(WIDTH - 40, 40, 150, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(16,185,129,0.10)";
  ctx.beginPath();
  ctx.arc(60, HEIGHT - 40, 180, 0, Math.PI * 2);
  ctx.fill();

  // Inner border
  ctx.strokeStyle = "rgba(34,197,94,0.35)";
  ctx.lineWidth = 6;
  roundRect(ctx, 28, 28, WIDTH - 56, HEIGHT - 56, 28);
  ctx.stroke();

  const cx = WIDTH / 2;

  // Graduation cap badge
  const capGrad = ctx.createLinearGradient(cx - 40, 70, cx + 40, 150);
  capGrad.addColorStop(0, "#22c55e");
  capGrad.addColorStop(1, "#059669");
  ctx.fillStyle = capGrad;
  roundRect(ctx, cx - 40, 74, 80, 80, 22);
  ctx.fill();
  ctx.font = "44px serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("🎓", cx, 116);

  // "Certificate of Completion"
  ctx.fillStyle = "#059669";
  ctx.font = "bold 22px Arial, sans-serif";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("CERTIFICATE OF COMPLETION", cx, 200);

  // "This certifies that"
  ctx.fillStyle = "#64748b";
  ctx.font = "26px Arial, sans-serif";
  ctx.fillText("This certifies that", cx, 250);

  // Name (gradient text)
  const nameGrad = ctx.createLinearGradient(cx - 250, 0, cx + 250, 0);
  nameGrad.addColorStop(0, "#16a34a");
  nameGrad.addColorStop(1, "#047857");
  ctx.fillStyle = nameGrad;
  ctx.font = "bold 64px Georgia, serif";
  // Trim overly long names to keep them on one line.
  let name = data.name;
  while (ctx.measureText(name).width > WIDTH - 160 && name.length > 3) {
    name = name.slice(0, -1);
  }
  if (name !== data.name) name = name.trimEnd() + "…";
  ctx.fillText(name, cx, 320);

  // Body line
  ctx.fillStyle = "#334155";
  ctx.font = "24px Arial, sans-serif";
  ctx.fillText(
    "has completed the BCH School curriculum,",
    cx,
    372,
  );
  ctx.fillText(
    "mastering Bitcoin Cash as everyday digital cash.",
    cx,
    406,
  );

  // Stats row
  const statsY = 480;
  const gap = 220;
  const drawStat = (x: number, value: string, label: string) => {
    ctx.fillStyle = "#059669";
    ctx.font = "bold 40px Arial, sans-serif";
    ctx.fillText(value, x, statsY);
    ctx.fillStyle = "#64748b";
    ctx.font = "16px Arial, sans-serif";
    ctx.fillText(label.toUpperCase(), x, statsY + 26);
  };
  drawStat(cx - gap, `${data.xp}`, `/ ${data.totalXp} XP`);
  drawStat(cx, `${data.badgeCount}`, "Badges");
  drawStat(cx + gap, "🎓", "Graduate");

  // Divider + footer
  ctx.strokeStyle = "rgba(100,116,139,0.25)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 320, statsY + 60);
  ctx.lineTo(cx + 320, statsY + 60);
  ctx.stroke();

  ctx.fillStyle = "#64748b";
  ctx.font = "18px Arial, sans-serif";
  ctx.fillText(
    `Awarded ${data.dateStr}  ·  BCH School  ·  bchschool.com`,
    cx,
    statsY + 92,
  );

  return canvas;
}

/** Render the certificate to a PNG Blob. */
export function certificateToBlob(data: CertificateData): Promise<Blob> {
  const canvas = drawCertificate(data);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Failed to render certificate image"));
    }, "image/png");
  });
}

/** A safe filename for the downloaded certificate. */
export function certificateFileName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return `bch-school-certificate-${slug || "graduate"}.png`;
}
